/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import adconfig from '../adconfig';
//import * as jsonp from '../jsonp';
import adloader from '../adloader';
import adrotation from '../adrotation';

/**
 * Implements openx ad server functionality
 * @class
 * @exports adserver/openx
 * @requires  module:utils
 * @requires  module:adconfig
 * @requires  module:adrotation
 * @requires  module:adloader
 */
class _openxad {
  _doc: Document;
  _win: window;
  _name: string;
  _defConfig: openxConfig;
  _config: openxConfig;
  _preloadJSPromise: Promise<*>;
  _isDialup: boolean;
  _brand: string;
  _QPVars: Object;
  constructor() {
    this._name = 'openx';
    this._doc = document;
    this._win = window;
    this._isDialup = false;
    this._brand = '';
    this._defConfig = {
      useFetch: true,
      lib: '//uol-d.openx.net/w/1.0/jstag', // default value is used for preloading
      preloadTimeout: 3000,
      url: '//uol-d.openx.net/w/1.0/arj?auid=',
      timeout: 10000
    };
    this._config = u.extend(true, this._defConfig);
    this._preloadJSPromise = Promise.resolve();
    this._QPVars = {};
  }

  /**
   * Initialize the object - called from adserver initialize
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._isDialup = u.getEvaluatedDialup(g.isDialup);
    this._brand = g.phInfo.brand;
    this._QPVars = g.QPVars;
    this._preloadJS();
  }

  _preloadJS() {
    // preload openx lib
    if (this._config.lib) {
      this._preloadJSPromise = u.preloadJS(
        this._doc,
        this._config.lib,
        this._config.preloadTimeout
      );
    }
  }

  /**
   * @global
   * @typedef {Object} openxConfig
   * @property {string} lib - ad server javascript url
   * @property {string} preloadTeimout - timeout for preload
   * @property {string} url - ad server url for fetching ad creatives
   * @property {number} timeout - timeout in milli secs
   */

  /**
   * Update config - called from adserver updateConfig
   * @param {openxConfig} o - adserver config from adconfig
   * @returns {undefined}
   */
  updateConfig(o: openxConfig) {
    this._config = u.extend(true, {}, this._defConfig, o);
  }

  /**
   * getName - getter for name
   * @return {string} name
   */
  getName(): string {
    return this._name;
  }

  _getAUID(pos: string = ''): string {
    if (!pos.length) {
      return '';
    }
    return adconfig.getPosSettings(pos).auid || '';
  }

  /**
   * Dynamically generate static openx creative using auid
   * @access private
   * @param {string} pos - Ad position
   * @returns {string} static openx creative
   */
  _getAdContent(pos: string): string {
    let auid = this._getAUID(pos);
    if (!auid) {
      u.logError(`${this._name} : auid not configured for ${pos}`);
      return '';
    }
    return `<script type='text/javascript'> \
              if (!window.OX_ads) { \
                OX_ads = []; \
              } \
              var v2 = {}; \
              if(typeof _QP === 'object') { \
                for(var i in _QP){ \
                  v2[i] = _QP[i]; \
                } \
              } \
              OX_ads.push({ auid : "${auid}", vars: v2}); \
            </script> \
            <script type='text/javascript'> \
              document.write('<scr'+'ipt src="${
                this._config.lib
              }"></scr'+'ipt>'); \
            </script>`;
  }

  _getCustomVars(pos: string, o: hbPartnerData, customVars: Object) {
    let params = o && o[pos] ? o[pos] : {};
    return u.extend(true, {}, customVars, params._CV, {
      _QP: this._getQueryParams(pos, o)
    });
  }

  _getOpenxParams() {
    return u.extend(true, {}, this._QPVars, {
      uolpagename: adconfig.getPageName(),
      uoldialup: this._isDialup,
      uolbrand: this._brand,
      uolrotate: adrotation.getRotationStatus()
    });
  }

  _getQueryParams(pos: string, o: hbPartnerData) {
    return u.extend(
      true,
      {},
      o && o[pos] ? o[pos]._QP : {},
      this._getOpenxParams()
    );
  }

  _loadAd(pos: string, content: string, customVars: Object): boolean | void {
    return adloader.load(pos, content, customVars);
  }

  /**
   * Loads openx static using adloader
   * @param {Array<string>} posList - position list array
   * @param {Object.<string, hbPosData>} o - header bidding return object
   * @param {Object} customVars - custom variable to be populated inside iframe
   * @returns {undefined}
   */
  loadAds(posList: Array<string>, o: hbPartnerData, customVars: Object) {
    posList = posList || [];
    /* eslint-disable promise/catch-or-return */
    this._preloadJSPromise.then(() =>
      posList.forEach(pos =>
        this._loadAd(
          pos,
          this._getAdContent(pos),
          this._getCustomVars(pos, o, customVars)
        )
      )
    );
    /* eslint-enable promise/catch-or-return */
  }
}

let openx = new _openxad();
export { openx, _openxad };
